import {
  ApplicationResourceDocument,
  type ApplicationResourceQuery,
} from "@graphql/application-resources/application-resources.g";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useDebounce } from "@uidotdev/usehooks";

import {
  InfiniteSelectBase,
  InfiniteSelectDataHook,
  SelectOption,
} from "@components/Selects/InfiniteSelectBase";

import { useEmployeeUsedApplicationsQuery } from "./InfiniteEmployeeUsedApplicationSelect.g";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE = 300;

const formatApplicationLabel = (
  productName?: string | null,
  vendorName?: string | null,
): string => {
  const product = productName?.trim() || "";
  const vendor = vendorName?.trim() || "";
  if (product && vendor) {
    return `${product} (${vendor})`;
  }
  return product || vendor;
};

const useInfiniteEmployeeUsedApplications = (
  clientEmployeeId: string,
  searchQuery: string,
  isOpen: boolean,
  disabled: boolean,
): InfiniteSelectDataHook => {
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE);

  const query = useInfiniteQuery({
    queryKey: [
      "InfiniteEmployeeUsedApplications",
      clientEmployeeId,
      debouncedSearchQuery,
    ],
    queryFn: async ({ pageParam }) => {
      const result = await useEmployeeUsedApplicationsQuery.fetcher({
        employeeId: clientEmployeeId,
        offset: pageParam as number,
        limit: PAGE_SIZE,
        ...(debouncedSearchQuery && {
          applicationName: debouncedSearchQuery,
        }),
      })();
      return result;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * PAGE_SIZE;
      const totalCount =
        lastPage?.clientEmployeeApplicationInteractions?.filteredCount || 0;

      if (currentOffset < totalCount) {
        return currentOffset;
      }
      return undefined;
    },
    enabled: isOpen && !disabled,
    staleTime: 5 * 60 * 1000,
  });

  const options = useMemo(() => {
    const allOptions: Array<{ id: string; label: string }> = [];

    if (query.data?.pages) {
      query.data.pages.forEach((page) => {
        const interactions =
          page?.clientEmployeeApplicationInteractions?.interactions || [];
        interactions.forEach((interaction) => {
          const resource = interaction?.primaryResource;
          if (resource) {
            allOptions.push({
              id: resource.id,
              label: formatApplicationLabel(
                resource.productName,
                resource.vendorName,
              ),
            });
          }
        });
      });
    }

    return allOptions;
  }, [query.data]);

  return {
    ...query,
    options,
  };
};

type Props = {
  clientEmployeeId: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const InfiniteEmployeeUsedApplicationSelect = ({
  clientEmployeeId,
  value,
  onChange,
  placeholder = "Search used applications...",
  disabled = false,
}: Props) => {
  const fetchSelectedOption = useCallback(
    async (id: string): Promise<SelectOption | null> => {
      try {
        const response = await fetch(
          import.meta.env.VITE_API_URL_GRAPHQL as string,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              query: ApplicationResourceDocument,
              variables: { id },
            }),
          },
        );

        const json = await response.json();

        if (json.errors) {
          console.error(
            "GraphQL error fetching used application:",
            json.errors,
          );
          return null;
        }

        const data = json.data as ApplicationResourceQuery;
        const resource = data?.applicationResource;

        if (resource) {
          return {
            id: resource.id,
            label: formatApplicationLabel(
              resource.productName,
              resource.vendorName,
            ),
          };
        }
      } catch (error) {
        console.error("Failed to fetch selected used application:", error);
      }
      return null;
    },
    [],
  );

  const useData = useMemo(() => {
    return (searchQuery: string, isOpen: boolean) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useInfiniteEmployeeUsedApplications(
        clientEmployeeId,
        searchQuery,
        isOpen,
        disabled,
      );
    };
  }, [clientEmployeeId, disabled]);

  return (
    <InfiniteSelectBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      useData={useData}
      fetchSelectedOption={fetchSelectedOption}
      loadingText="Loading applications..."
      noResultsText="No used applications found"
      loadingMoreText="Loading more..."
      disabled={disabled}
    />
  );
};
